import * as Sentry from '@sentry/browser'
import { useQuery } from '@tanstack/react-query'
import { getCampaignsApiUrl } from 'src/config'
import { parseCampaigns } from 'src/features/campaignBanner/parseCampaigns'
import { queryKeyPrefix } from 'src/features/tanStackQuery/queryKeyPrefix'

/** キャンペーンの入れ替えは日単位で行われるため、1 時間は取得し直さない */
const STALE_TIME_MS = 60 * 60 * 1000

const fetchCampaigns = async (): Promise<unknown> => {
  const response = await fetch(getCampaignsApiUrl())
  if (!response.ok) {
    throw new Error(
      `レスポンスが 2xx ではありません: status=${response.status}`,
    )
  }

  return response.json()
}

/**
 * 掲載中のキャンペーンを取得する。キャンペーンがなければ undefined を返す。
 *
 * 取得先はプロダクト共通のキャンペーン配信 API であり、harami の API とは別系統。
 * 認証も行わないため、グローバルな認証エラーハンドリングの対象から外す。
 *
 * バナーの不調で画面にエラーを出すのは避けたいので、失敗しても画面には何も出さない。
 * 代わりに Sentry へ送り、配信側の異常に気づけるようにする。
 *
 * 配信 API がまだ存在しないため、動かすのはローカルのみ。
 * API が用意できたら enabled を外す。
 */
export const useQueryCampaigns = () =>
  useQuery({
    queryKey: [queryKeyPrefix.campaignBanner],
    meta: { skipGlobalErrorHandler: true },
    queryFn: async () => {
      const response = await fetchCampaigns().catch((cause: unknown) => {
        const error = new Error('キャンペーン情報の取得に失敗しました', {
          cause,
        })
        Sentry.captureException(error)
        throw error
      })

      if (!Array.isArray(response)) {
        Sentry.captureException(
          new Error('キャンペーン情報が配列ではありません'),
        )
        return []
      }

      const campaigns = parseCampaigns(response)

      // 想定と違う形のキャンペーンは捨てるため、捨てたことに配信側が気づけるようにする
      const droppedCount = response.length - campaigns.length
      if (droppedCount > 0) {
        Sentry.captureException(
          new Error(
            `キャンペーン情報の形式が想定と異なります: ${droppedCount}件を破棄しました`,
          ),
        )
      }

      return campaigns
    },
    // 掲載中のキャンペーンは同時に 1 つまでという運用のため、先頭の 1 件だけを採用する
    // もしも、複数キャンペーンを同時に掲載する運用に変わった場合は、ここを変更する必要がある
    select: campaigns => campaigns[0],
    staleTime: STALE_TIME_MS,
  })
